/**
 * VendorDirectoryLinks
 *
 * "All restaurants on Lusuku": a plain A to Z list of links to every public
 * storefront of one kind, at the foot of its directory page.
 *
 * Why it exists (16 Oct 2026): the cards above show what is useful to a
 * shopper right now (open, nearby, near the top), so a vendor that was
 * closed, outside the shopper's area or further down had no link on the
 * page, and Search Console knew several storefronts only from the sitemap.
 * Google ranks a page it reaches through links better than one it only
 * finds in a sitemap. This list is the sitemap's own list of storefronts
 * (GET /seo/storefronts, Lusuku_Dashboard_Node-Server/utils/sitemapBuilder.js),
 * so every vendor the sitemap names is one link away from its directory page,
 * for shoppers and search engines alike.
 *
 * Renders nothing until the list arrives, or if it cannot be fetched: the
 * cards above still work without it.
 */
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CrawlLink from "./CrawlLink";
import { publicRequest } from "../api/requestMethods";

export default function VendorDirectoryLinks({ vertical, title }) {
  const [storefronts, setStorefronts] = useState([]);

  useEffect(() => {
    let live = true;
    publicRequest
      .get("/seo/storefronts", { params: { vertical } })
      .then((res) => {
        const list = res && res.data && res.data.found ? res.data.data : [];
        if (live) setStorefronts(Array.isArray(list) ? list.filter((s) => s && s.path && s.name) : []);
      })
      .catch(() => {
        if (live) setStorefronts([]);
      });
    return () => {
      live = false;
    };
  }, [vertical]);

  if (!storefronts.length) return null;

  const headingId = `vendor-directory-${vertical}`;
  return (
    <Section aria-labelledby={headingId}>
      <Heading id={headingId}>{title}</Heading>
      <List>
        {storefronts.map((s) => (
          <Item key={s.path}>
            <CrawlLink to={s.path}>{s.name}</CrawlLink>
          </Item>
        ))}
      </List>
    </Section>
  );
}

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto 2.5rem;
  padding: 1.5rem 1.5rem 1.25rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  @media (max-width: 768px) {
    margin: 0 1rem 2rem;
    padding: 1.25rem 1rem 1rem;
  }
`;

const Heading = styled.h2`
  margin: 0 0 0.875rem;
  font-family: "Poppins", sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  columns: 4 200px;
  column-gap: 1.5rem;
`;

const Item = styled.li`
  break-inside: avoid;
  padding: 0.3rem 0;
  font-size: 0.9rem;
  line-height: 1.35;
  color: #374151;

  a:hover,
  a:focus-visible {
    color: #0d47a1;
    text-decoration: underline !important;
  }
`;
